// « Soldes clients » — what each customer owes us (docs/PMS-LEDGER-UI-BRIEF.md §2, panel 3).
//
// Read-only. DEBIT-positive, because staff read it (operator, 2026-10-06): the customer who owes is
// « À payer », the one who paid in advance is « Avance », zero is « Soldé ». Settled customers are
// HIDDEN by default, with a switch. A customer opens its movements, newest first, with the running
// balance.
//
// Honesty: the balances run « depuis l'ouverture du livre » (the book has no opening entry) and are not
// comparable with bosa's FEC-based ones, and the screen says so. A list is never cut silently: « n sur N »
// with a « Charger la suite » button, and a debtor total is only given when everyone is loaded. A missing
// right is « Droit manquant », a failed read says so and retries: neither looks like « aucun client ».

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  debtorTotal,
  dueReading,
  formatAmount,
  formatDay,
  formatDayLong,
  holderDisplayName,
  journalLabel,
  todayParis,
  type CustomerBalance,
  type CustomerMovement,
  type LedgerReader,
} from '@lemontaigu/fin-core';
import { Document } from './Document.js';

const PAGE = 200;
/** The most movements asked for one customer; reaching it means there may be older ones. */
const MOVEMENT_LIMIT = 500;

// ── the pure views ──────────────────────────────────────────────────────────

export function SoldesView({
  rows,
  totalHolders,
  includeSettled,
  asOf,
  onOpen,
  onMore,
  loadingMore,
}: {
  rows: readonly CustomerBalance[];
  totalHolders: number;
  includeSettled: boolean;
  asOf: string;
  onOpen: (holder: CustomerBalance) => void;
  onMore: () => void;
  loadingMore: boolean;
}) {
  const since = rows[0]?.since;
  const all = rows.length >= totalHolders;
  const total = debtorTotal(rows);
  return (
    <div data-testid="fin-soldes">
      <p className="muted" data-testid="fin-soldes-since">
        {since ? `Depuis l’ouverture du livre (${formatDayLong(since)})` : 'Depuis l’ouverture du livre'}, au {formatDay(asOf)}. Ces soldes ne sont
        pas comparables à ceux du FEC de bosa : le livre d’essai n’a pas d’écriture d’ouverture.
      </p>
      {rows.length === 0 ? (
        <p className="muted" data-testid="fin-soldes-empty">
          {includeSettled ? 'Aucun client n’a de mouvement dans le livre.' : 'Aucun client ne doit rien et aucun n’a d’avance. Cochez « afficher les clients soldés » pour les voir.'}
        </p>
      ) : (
        <div className="pms-table-wrap">
          <table className="pms-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Client</th>
                <th>Dernier mouvement</th>
                <th className="fin-num">À payer</th>
                <th className="fin-num">Avance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const due = dueReading(r.dueCents);
                return (
                  <tr key={r.holderRef} data-holder={r.holderRef} className="fin-click" onClick={() => onOpen(r)}>
                    <td>
                      <button type="button" className="fin-link" onClick={(e) => { e.stopPropagation(); onOpen(r); }}>
                        {r.holderRef}
                      </button>
                    </td>
                    <td>{holderDisplayName(r)}</td>
                    <td>{r.lastMovement ? formatDay(r.lastMovement) : ''}</td>
                    <td className="fin-num">{due.kind === 'owes' ? formatAmount(due.amountCents) : due.kind === 'settled' ? 'soldé' : ''}</td>
                    <td className="fin-num">{due.kind === 'advance' ? formatAmount(due.amountCents) : ''}</td>
                  </tr>
                );
              })}
            </tbody>
            {all && (
              <tfoot>
                <tr data-testid="fin-soldes-total">
                  <th colSpan={3}>Total ({rows.filter((r) => r.holderKind !== 'anonymous_outlet').length} clients, comptes de passage exclus)</th>
                  <th className="fin-num">{formatAmount(total.owedCents)}</th>
                  <th className="fin-num">{formatAmount(total.advanceCents)}</th>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      <p className="muted" data-testid="fin-soldes-count">
        {rows.length} sur {totalHolders}
        {!all && ' : le total n’est pas donné tant que tous les clients ne sont pas chargés.'}
      </p>
      {!all && (
        <button type="button" onClick={onMore} disabled={loadingMore}>
          {loadingMore ? 'Chargement…' : 'Charger la suite'}
        </button>
      )}
    </div>
  );
}

export function MouvementsView({
  holder,
  movements,
  onClose,
  onPiece,
}: {
  holder: CustomerBalance;
  movements: readonly CustomerMovement[];
  onClose: () => void;
  /** Opens the document (invoice number) of a movement. Absent: the number is plain text. */
  onPiece?: (piece: string) => void;
}) {
  const due = dueReading(holder.dueCents);
  const truncated = movements.length >= MOVEMENT_LIMIT;
  return (
    <section className="fin-mouvements" data-testid="fin-mouvements" aria-label={`Mouvements de ${holder.holderRef}`}>
      <div className="fin-mouvements-head">
        <h4>
          {holderDisplayName(holder)} <span className="muted">({holder.holderRef})</span>
        </h4>
        <button type="button" onClick={onClose}>Fermer</button>
      </div>
      <p data-testid="fin-mouvements-balance">
        Solde : <strong>{due.kind === 'settled' ? 'soldé' : `${due.label} ${formatAmount(due.amountCents)}`}</strong>
      </p>
      {truncated && (
        <p className="pms-warn" data-testid="fin-mouvements-truncated">
          Les {MOVEMENT_LIMIT} mouvements les plus récents : il peut en exister de plus anciens.
        </p>
      )}
      {movements.length === 0 ? (
        <p className="muted">Aucun mouvement.</p>
      ) : (
        <div className="pms-table-wrap">
          <table className="pms-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Journal</th>
                <th>Pièce</th>
                <th>Libellé</th>
                <th className="fin-num">Débit</th>
                <th className="fin-num">Crédit</th>
                <th className="fin-num">Solde cumulé</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((m, i) => (
                <tr key={`${m.entryId}|${i}`}>
                  <td>{formatDay(m.entryDate)}</td>
                  <td>{journalLabel(m.journal)}</td>
                  <td>
                    {onPiece ? (
                      <button type="button" className="fin-link" onClick={() => onPiece(m.pieceRef)}>{m.pieceRef}</button>
                    ) : (
                      m.pieceRef
                    )}
                  </td>
                  <td>{m.label ?? ''}</td>
                  <td className="fin-num">{m.debitCents === 0 ? '' : formatAmount(m.debitCents)}</td>
                  <td className="fin-num">{m.creditCents === 0 ? '' : formatAmount(m.creditCents)}</td>
                  <td className="fin-num">{formatAmount(m.runningDueCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── the container ───────────────────────────────────────────────────────────

type List =
  | { phase: 'loading' }
  | { phase: 'ready'; rows: readonly CustomerBalance[]; totalHolders: number }
  | { phase: 'forbidden' }
  | { phase: 'error'; message: string };

type Detail =
  | { phase: 'loading'; holder: CustomerBalance }
  | { phase: 'ready'; holder: CustomerBalance; movements: readonly CustomerMovement[] }
  | { phase: 'forbidden'; holder: CustomerBalance }
  | { phase: 'error'; holder: CustomerBalance; message: string };

export function SoldesClients({ ledger, book, now }: { ledger: LedgerReader; book: string; now?: Date }) {
  const [includeSettled, setIncludeSettled] = useState(false);
  const [asOf, setAsOf] = useState(() => todayParis(now ?? new Date()));
  const [list, setList] = useState<List>({ phase: 'loading' });
  const [loadingMore, setLoadingMore] = useState(false);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [piece, setPiece] = useState<string | null>(null);
  // A late answer to an earlier question must not overwrite a newer one (a switch toggled twice in a row).
  const ticket = useRef(0);

  const load = useCallback(async () => {
    const mine = ++ticket.current;
    setList({ phase: 'loading' });
    setDetail(null);
    const r = await ledger.customerBalances(book, { asOf, includeSettled, limit: PAGE });
    if (mine !== ticket.current) return;
    if (r.ok) setList({ phase: 'ready', rows: r.value.rows, totalHolders: r.value.totalHolders });
    else if (r.error.kind === 'forbidden') setList({ phase: 'forbidden' });
    else setList({ phase: 'error', message: r.error.message });
  }, [ledger, book, asOf, includeSettled]);

  useEffect(() => {
    void load();
  }, [load]);

  const more = async () => {
    if (list.phase !== 'ready' || list.rows.length === 0) return;
    const mine = ticket.current;
    setLoadingMore(true);
    const after = list.rows[list.rows.length - 1]!.holderRef;
    const r = await ledger.customerBalances(book, { asOf, includeSettled, limit: PAGE, after });
    setLoadingMore(false);
    if (mine !== ticket.current) return;
    if (r.ok) setList({ phase: 'ready', rows: [...list.rows, ...r.value.rows], totalHolders: r.value.totalHolders });
    else if (r.error.kind === 'forbidden') setList({ phase: 'forbidden' });
    else setList({ phase: 'error', message: r.error.message });
  };

  const open = async (holder: CustomerBalance) => {
    setPiece(null);
    setDetail({ phase: 'loading', holder });
    const r = await ledger.customerLedger(book, holder.holderRef, { limit: MOVEMENT_LIMIT });
    if (r.ok) setDetail({ phase: 'ready', holder, movements: r.value });
    else if (r.error.kind === 'forbidden') setDetail({ phase: 'forbidden', holder });
    else setDetail({ phase: 'error', holder, message: r.error.message });
  };

  return (
    <section className="fin-soldes-panel" aria-label="Soldes clients">
      <h3>Soldes clients</h3>
      <div className="fin-controls">
        <label>
          <input type="checkbox" checked={includeSettled} onChange={(e) => setIncludeSettled(e.target.checked)} /> Afficher les clients soldés
        </label>
        <label>
          Au <input type="date" value={asOf} onChange={(e) => e.target.value && setAsOf(e.target.value)} />
        </label>
      </div>
      {list.phase === 'loading' && <p className="muted" data-testid="fin-soldes-loading">Chargement des soldes…</p>}
      {list.phase === 'forbidden' && (
        <div className="pms-warn" role="alert" data-testid="fin-soldes-forbidden">
          Droit manquant : les soldes clients sont réservés à l’administrateur.
        </div>
      )}
      {list.phase === 'error' && (
        <div className="pms-warn" role="alert" data-testid="fin-soldes-error">
          <p>Les soldes sont illisibles : ne rien en conclure. ({list.message})</p>
          <button type="button" onClick={() => void load()}>Réessayer</button>
        </div>
      )}
      {list.phase === 'ready' && (
        <SoldesView
          rows={list.rows}
          totalHolders={list.totalHolders}
          includeSettled={includeSettled}
          asOf={asOf}
          onOpen={(h) => void open(h)}
          onMore={() => void more()}
          loadingMore={loadingMore}
        />
      )}
      {detail?.phase === 'loading' && <p className="muted" data-testid="fin-mouvements-loading">Chargement des mouvements…</p>}
      {detail?.phase === 'forbidden' && (
        <div className="pms-warn" role="alert" data-testid="fin-mouvements-forbidden">Droit manquant : les mouvements sont réservés à l’administrateur.</div>
      )}
      {detail?.phase === 'error' && (
        <div className="pms-warn" role="alert" data-testid="fin-mouvements-error">
          <p>Les mouvements sont illisibles : ne rien en conclure. ({detail.message})</p>
          <button type="button" onClick={() => void open(detail.holder)}>Réessayer</button>
        </div>
      )}
      {detail?.phase === 'ready' && (
        <MouvementsView holder={detail.holder} movements={detail.movements} onClose={() => { setDetail(null); setPiece(null); }} onPiece={setPiece} />
      )}
      {detail?.phase === 'ready' && piece && <Document ledger={ledger} book={book} piece={piece} onClose={() => setPiece(null)} />}
    </section>
  );
}
